import { p as proxyCustomElement, H, c as createEvent, h, F as Fragment } from './index.js';
import { f as forwardEvent } from './p-rUtJRH95.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { g as getBoxAttributes } from './p-CXN6CHBw.js';
import { d as defineCustomElement$5 } from './p-BbTg3Car.js';
import { d as defineCustomElement$4 } from './p-BlW2joRF.js';
import { d as defineCustomElement$3 } from './p-Dr61d_nc.js';
import { d as defineCustomElement$2 } from './p-_P4kPR3l.js';
import { d as defineCustomElement$1 } from './p-CdhcNtiX.js';
import { d as debounce } from './p-Dg7s1ivP.js';

const textareaCss = ":host{display:flex;position:relative}:host .box{align-items:center;background-color:var(--jw-theme-form-field-default-bg-color);border:1px solid var(--jw-theme-form-border-regular-default-border-color);border-radius:.25rem;color:var(--jw-theme-form-field-filled-color);display:flex;font-family:Open Sans;font-size:.875rem;font-size:inherit;font-weight:400;font-weight:inherit;gap:.5rem;height:100%;letter-spacing:-.0004em;line-height:1.25rem;min-width:64px;padding:0;transition:border-color .3s ease;width:100%}:host .box[disabled]{background-color:var(--jw-theme-form-field-disabled-bg-color);color:var(--jw-theme-surface-text-disabled-color);cursor:not-allowed}:host .box:not([disabled]):hover{border-color:var(--jw-theme-form-border-regular-hover-border-color)}:host .box:not([disabled]):focus,:host .box:not([disabled]):focus-within{border-color:var(--jw-theme-form-border-regular-focus-border-color);outline:var(--jw-theme-effects-focus-default-outline)}:host:focus,:host:focus-visible{outline:none}.inner{display:flex;width:100%}:host([is-empty]) .box{color:var(--jw-theme-form-field-placeholder-color)}:host([error-type=error]) .box{border-color:var(--jw-theme-form-border-error-default-border-color)}:host([error-type=error]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-error-hover-border-color)}:host([error-type=warning]) .box{border-color:var(--jw-theme-form-border-warning-default-border-color)}:host([error-type=warning]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-warning-hover-border-color)}:host .input::placeholder{color:var(--jw-theme-form-field-placeholder-color)}:host .input::-ms-clear,:host .input::-webkit-clear-button,:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{appearance:none;display:none;margin:0}:host .input::-webkit-search-cancel-button,:host .input::-webkit-search-decoration,:host .input::-webkit-search-results-button,:host .input::-webkit-search-results-decoration{appearance:none}:host .input::-webkit-calendar-picker-indicator{background:none;display:none}:host .input[type=number]{appearance:textfield}:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{appearance:none;margin:0}:host .input:-webkit-autofill,:host .input:-webkit-autofill:first-line{font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem}:host .input:-webkit-autofill,:host .input:-webkit-autofill:focus,:host .input:-webkit-autofill:hover{box-shadow:0 0 0 1000px var(--jw-theme-form-field-default-bg-color) inset;-webkit-text-fill-color:var(--jw-theme-form-field-filled-color)}:host .input:-webkit-autofill[disabled]{box-shadow:0 0 0 1000px var(--jw-theme-form-field-disabled-bg-color) inset}:host .input[disabled]{cursor:not-allowed}:host .input[disabled]::placeholder{color:var(--jw-theme-surface-text-disabled-color)}:host .input{background:none;border:none;box-shadow:none;color:var(--jw-theme-form-field-filled-color);flex:1;float:left;font-family:Open Sans;font-size:.875rem;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;margin:0;min-width:0;padding:0;width:100%}:host .input:focus,:host .input:focus-visible{outline:none}:host([is-empty]) .input{color:var(--jw-theme-form-field-placeholder-color)}:host([disabled]) .input{color:var(--jw-theme-surface-text-disabled-color)}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host .box{position:relative}:host .input{padding:.5rem;resize:none;transition:all .1s linear}:host .char-count{font-size:.75rem;font-weight:400}:host .char-count.error{color:var(--jw-theme-form-validation-error-color)}:host([resizable]) .input{resize:vertical}";

const WuiTextarea = /*@__PURE__*/ proxyCustomElement(class WuiTextarea extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        /**
         * Placeholder
         */
        this.placeholder = '';
        /**
         * Read only
         */
        this.readonly = false;
        /**
         * If disabled
         */
        this.disabled = false;
        /**
         * If resizable
         */
        this.resizable = false;
        this.errorMaxLength = false;
        this.handleChange = (e) => {
            e.stopPropagation();
            const target = e.target;
            if (!target) {
                return;
            }
            this.value = target.value;
            this.dirty = true;
            this.checkValidity();
            this.updateMaxLengthErrorState();
            this.updateHeight();
            // Trigger event
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        // Default HTML validity max length check does not work because
        // a user can not input more than max length. However, it's
        // still possible to populate the input with a value that
        // exceeds max length.
        this.updateMaxLengthErrorState = () => {
            if (this.maxLength && this.value) {
                this.errorMaxLength = this.value.length > this.maxLength;
            }
        };
        this.updateHeight = () => {
            if (this.resizable) {
                // Don't automatically resize when manually resizable
                return;
            }
            if (!this.inputElm) {
                return;
            }
            this.inputElm.style.height = 'auto';
            const newHeight = this.inputElm.scrollHeight + (this.maxLength ? 32 : 0);
            this.inputElm.style.height = `${newHeight}px`;
        };
        this.debouncedUpdateHeight = debounce(this.updateHeight, 100);
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    componentDidLoad() {
        window.addEventListener('resize', this.debouncedUpdateHeight);
        this.updateMaxLengthErrorState();
        this.updateHeight();
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this.debouncedUpdateHeight);
    }
    componentDidUpdate() {
        this.updateHeight();
    }
    focus() {
        this.inputElm?.focus();
    }
    renderInner() {
        return (h(Fragment, null, h("div", { class: "box", ...getBoxAttributes(this) }, h("textarea", { class: "input", ref: this.setInputRef, onInput: this.handleChange, placeholder: this.placeholder, disabled: this.disabled, readonly: this.readonly, cols: this.cols, rows: this.rows, required: !!this.required, minLength: this.minLength, maxLength: this.maxLength, value: this.value, name: this.name, onFocus: (e) => {
                forwardEvent(e, this.elm);
            }, onBlur: (e) => {
                forwardEvent(e, this.elm);
            } })), this.maxLength && (h("span", { class: `char-count ${this.errorMaxLength ? 'error' : ''}`, slot: "post-description" }, (this.value ?? '').length, "/", this.maxLength))));
    }
    render() {
        return renderFormField(this, this.renderInner(), { host: { tabIndex: 0 } });
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return textareaCss; }
}, [257, "wui-textarea", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "name": [1],
        "placeholder": [1],
        "readonly": [516],
        "disabled": [516],
        "minLength": [2, "min-length"],
        "maxLength": [2, "max-length"],
        "cols": [2],
        "rows": [2],
        "resizable": [516],
        "customValidation": [32],
        "showValidity": [32],
        "errorMaxLength": [32]
    }, undefined, {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-textarea", "wui-box", "wui-form-field", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-textarea":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiTextarea);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiTextarea as W, defineCustomElement as d };
